import { useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export default function Auth() {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters";
    if (!/[a-z]/.test(password)) return "Password must contain a lowercase letter";
    if (!/[A-Z]/.test(password)) return "Password must contain an uppercase letter";
    if (!/\d/.test(password)) return "Password must contain a number";
    return null;
  };

  const validateRegister = () => {
    const newErrors = {};
    if (!registerData.name.trim()) newErrors.name = "Name is required";
    if (!registerData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(registerData.email)) newErrors.email = "Invalid email format";
    
    const passwordError = validatePassword(registerData.password);
    if (passwordError) newErrors.password = passwordError;
    
    if (registerData.password !== registerData.confirmPassword) newErrors.confirmPassword = "Passwords don't match";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      await login(loginData.email, loginData.password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      const message = err.response?.data?.detail || "Invalid email or password";
      setErrors({ login: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateRegister()) return;
    
    setLoading(true);
    try {
      await register(registerData.name, registerData.email, registerData.password);
      toast.success("Welcome to Paradise Borrowed! Your account has been created.");
      navigate("/dashboard");
    } catch (err) {
      const message = err.response?.data?.detail || "Registration failed. Please try again.";
      setErrors({ register: message });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: resetEmail });
      setResetSent(true);
      toast.success("If an account exists with this email, you'll receive reset instructions.");
    } catch (err) {
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src="/logo.png" alt="Paradise Borrowed" className="w-20 h-auto mx-auto mb-4" />
            <h1 className="font-heading font-bold text-2xl text-stone-900">Reset Your Password</h1>
            <p className="text-stone-500 text-sm mt-1">
              {resetSent ? "Check your email for instructions" : "Enter your email to receive reset instructions"}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
            {resetSent ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-medium text-stone-900 mb-2">Email Sent!</h3>
                <p className="text-sm text-stone-500 mb-6">
                  If an account exists for {resetEmail}, you'll receive password reset instructions shortly.
                </p>
                <Button 
                  onClick={() => { setShowForgotPassword(false); setResetSent(false); setResetEmail(""); }}
                  className="w-full h-12 rounded-full bg-coral hover:bg-coral text-white"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="reset-email">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input 
                      id="reset-email" 
                      type="email" 
                      required 
                      placeholder="you@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="h-12 pl-10 rounded-xl"
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full h-12 rounded-full bg-coral hover:bg-coral text-white btn-press"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="w-full text-sm text-stone-500 hover:text-stone-700 transition-colors"
                >
                  ← Back to Login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center px-4 py-12" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Paradise Borrowed" className="w-20 h-auto mx-auto mb-4" />
          <h1 className="font-heading font-bold text-2xl text-stone-900">Welcome to Paradise Borrowed™</h1>
          <p className="text-stone-500 text-sm mt-1">Borrow what you need, lend what you have</p>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-6">
          <Tabs defaultValue={defaultTab}>
            <TabsList className="w-full mb-6 h-12 p-1 bg-stone-100 rounded-xl">
              <TabsTrigger value="login" className="flex-1 h-10 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="auth-login-tab">
                Log In
              </TabsTrigger>
              <TabsTrigger value="register" className="flex-1 h-10 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="auth-register-tab">
                Sign Up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                {errors.login && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                    {errors.login}
                  </div>
                )}
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input 
                      id="login-email" 
                      type="email" 
                      required 
                      placeholder="you@example.com"
                      value={loginData.email} 
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      className="h-12 pl-10 rounded-xl" 
                      data-testid="login-email-input" 
                    />
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password">Password</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-xs text-coral hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input 
                      id="login-password" 
                      type={showPassword ? "text" : "password"} 
                      required 
                      placeholder="Enter your password"
                      value={loginData.password} 
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="h-12 pl-10 pr-10 rounded-xl" 
                      data-testid="login-password-input" 
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full h-12 rounded-full bg-coral hover:bg-coral text-white btn-press" 
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Logging in...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Log In <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {errors.register && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                    {errors.register}
                  </div>
                )}
                <div>
                  <Label htmlFor="register-name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input 
                      id="register-name" 
                      required 
                      placeholder="Your full name"
                      value={registerData.name} 
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      className={`h-12 pl-10 rounded-xl ${errors.name ? 'border-red-300' : ''}`}
                      data-testid="register-name-input" 
                    />
                  </div>
                  {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="register-email">Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input 
                      id="register-email" 
                      type="email" 
                      required 
                      placeholder="you@example.com"
                      value={registerData.email} 
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      className={`h-12 pl-10 rounded-xl ${errors.email ? 'border-red-300' : ''}`}
                      data-testid="register-email-input" 
                    />
                  </div>
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="register-password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input 
                      id="register-password" 
                      type={showPassword ? "text" : "password"} 
                      required 
                      placeholder="Min 8 chars, uppercase, lowercase, number"
                      value={registerData.password} 
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      className={`h-12 pl-10 pr-10 rounded-xl ${errors.password ? 'border-red-300' : ''}`}
                      data-testid="register-password-input" 
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
                </div>
                <div>
                  <Label htmlFor="register-confirm">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input 
                      id="register-confirm" 
                      type={showPassword ? "text" : "password"} 
                      required 
                      placeholder="Confirm your password"
                      value={registerData.confirmPassword} 
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      className={`h-12 pl-10 rounded-xl ${errors.confirmPassword ? 'border-red-300' : ''}`}
                      data-testid="register-confirm-input" 
                    />
                  </div>
                  {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
                </div>
                <Button 
                  type="submit" 
                  disabled={loading} 
                  className="w-full h-12 rounded-full bg-coral hover:bg-coral text-white btn-press" 
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Creating account...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Create Account <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <p className="text-xs text-stone-400 text-center mt-6">
            By continuing, you agree to our{" "}
            <Link to="/terms-of-service" className="text-coral hover:underline">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy-policy" className="text-coral hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

// Clean signup form - ID verification moved to My Profile page
